import { create } from 'zustand';
import axiosClient from '../../src/services/api';
import { getSocket } from '../utils/socket';
import useAuthStore from './useAuthStore';

const useMessageStore = create((set, get) => ({
  conversations: [],
  currentConversation: null,
  messages: [],
  isLoading: false,
  error: null,
  typingUsers: {},

  // Actions
  fetchConversations: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/messages/conversations');
      set({
        conversations: response.data.formattedConversations,
        isLoading: false
      });
      return response.data.formattedConversations;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch conversations',
        isLoading: false
      });
      return null;
    }
  },

  fetchConversationMessages: async (conversationId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get(`/messages/conversations/${conversationId}`);

      const sortedMessages = [...response.data.data]
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      set({
        messages: sortedMessages,
        currentConversation: conversationId,
        isLoading: false
      });

      const socket = getSocket();
      if (socket?.connected) {
        socket.emit('joinConversation', conversationId);
      }

      return sortedMessages;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch messages',
        isLoading: false
      });
      return null;
    }
  },

  createDirectConversation: async (recipientId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post('/messages/conversations', { recipientId });
      set({ isLoading: false });
      return response.data.conversation;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to create conversation',
        isLoading: false
      });
      return null;
    }
  },

  sendMessage: async (conversationId, messageData) => {
    const { user } = useAuthStore.getState();
    if (!user) return null;

    try {
      const tempId = 'temp-' + Date.now();
      const optimisticMessage = {
        _id: tempId,
        ...messageData,
        sender: { _id: user._id, name: user.name },
        createdAt: new Date().toISOString(),
        status: 'sending'
      };

      set(state => ({
        messages: [...state.messages, optimisticMessage]
      }));

      const response = await axiosClient.post(
        `/messages/conversations/${conversationId}`,
        messageData
      );

      set(state => ({
        messages: state.messages.map(m =>
          m._id === tempId ? { ...response.data.message, status: 'sent' } : m
        )
      }));

      const socket = getSocket();
      if (socket?.connected) {
        socket.emit('typingStop', { conversationId, userId: user._id });
      }

      return response.data.message;

    } catch (error) {
      set(state => ({
        messages: state.messages.map(m =>
          m._id?.startsWith('temp-') ? { ...m, status: 'failed' } : m
        )
      }));
      set({
        error: error.response?.data?.error || 'Failed to send message'
      });
      return null;
    }
  },

  markMessagesAsRead: async (conversationId) => {
    try {
      await axiosClient.post(`/messages/conversations/${conversationId}/read`);
      set((state) => ({
        conversations: state.conversations.map(conv =>
          conv._id === conversationId
            ? { ...conv, unreadCount: 0 }
            : conv
        )
      }));
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  },

  // ✅ NEW: Delete Message Action
  deleteMessage: async (messageId) => {
    const { user } = useAuthStore.getState();
    if (!user) return null;

    try {
      // Optimistic update
      set(state => ({
        messages: state.messages.map(m =>
          m._id === messageId ? { ...m, isDeleted: true } : m
        )
      }));

      const response = await axiosClient.delete(`/messages/${messageId}`);

      // Emit via socket
      const socket = getSocket();
      if (socket?.connected) {
        const message = get().messages.find(m => m._id === messageId);
        if (message) {
          socket.emit('messageDeleted', {
            conversationId: message.conversationId,
            messageId: message._id
          });
        }
      }

      return response.data;

    } catch (error) {
      // Revert on error
      console.error('Failed to delete message:', error);
      set(state => ({
        messages: state.messages.map(m =>
          m._id === messageId ? { ...m, isDeleted: false } : m
        )
      }));
      return null;
    }
  },

  editMessage: async (messageId, newContent) => {
    const { user } = useAuthStore.getState();
    if (!user) return null;

    try {
      set(state => ({
        messages: state.messages.map(m =>
          m._id === messageId
            ? { ...m, content: newContent, isEdited: true, editedAt: new Date().toISOString() }
            : m
        )
      }));

      const response = await axiosClient.put(`/messages/${messageId}`, { content: newContent });

      set(state => ({
        messages: state.messages.map(m =>
          m._id === messageId ? response.data.data : m
        )
      }));

      const socket = getSocket();
      if (socket?.connected) {
        const message = response.data.data;
        socket.emit('messageEdited', {
          conversationId: message.conversationId,
          messageId: message._id,
          content: message.content,
          editedAt: message.editedAt
        });
      }

      return response.data.data;
    } catch (error) {
      console.error('Failed to edit message:', error);
      set(state => ({
        messages: state.messages.map(m =>
          m._id === messageId
            ? { ...m, content: m.originalContent || m.content, isEdited: false, editedAt: undefined }
            : m
        )
      }));
      return null;
    }
  },

  toggleReaction: async (messageId, emoji) => {
    const { user } = useAuthStore.getState();
    if (!user) return null;

    try {
      set(state => ({
        messages: state.messages.map(m => {
          if (m._id !== messageId) return m;

          const existing = m.reactions?.find(r => r.userId === user._id && r.emoji === emoji);
          let newReactions;

          if (existing) {
            newReactions = m.reactions.filter(r => !(r.userId === user._id && r.emoji === emoji));
          } else {
            newReactions = [
              ...(m.reactions || []),
              { emoji, userId: user._id, userName: user.name }
            ];
          }

          return { ...m, reactions: newReactions };
        })
      }));

      const response = await axiosClient.post(`/messages/${messageId}/reaction`, { emoji });

      set(state => ({
        messages: state.messages.map(m =>
          m._id === messageId ? { ...m, reactions: response.data.data.reactions } : m
        )
      }));

      const socket = getSocket();
      if (socket?.connected) {
        const message = get().messages.find(m => m._id === messageId);
        if (message) {
          socket.emit('reactionUpdated', {
            conversationId: message.conversationId,
            messageId,
            reactions: response.data.data.reactions
          });
        }
      }

      return response.data.data;
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
      // Don't revert — let user retry or refresh
      return null;
    }
  },

  // Real-time handlers
  handleNewMessage: (message) => {
    set((state) => {
      const exists = state.messages.some(m => m._id === message._id);
      if (exists) return state;

      const updated = [...state.messages, message].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );

      return { messages: updated };
    });
  },

  handleTypingStart: (conversationId, userId, userName) => {
    set((state) => {
      const current = state.typingUsers[conversationId]?.[userId];
      if (current?.timeoutId) {
        clearTimeout(current.timeoutId);
      }

      const timeoutId = setTimeout(() => {
        get().handleTypingStop(conversationId, userId);
      }, 5000);

      return {
        typingUsers: {
          ...state.typingUsers,
          [conversationId]: {
            ...state.typingUsers[conversationId],
            [userId]: { userName, timestamp: Date.now(), timeoutId }
          }
        }
      };
    });
  },

  handleTypingStop: (conversationId, userId) => {
    set((state) => {
      const userData = state.typingUsers[conversationId]?.[userId];
      if (userData?.timeoutId) {
        clearTimeout(userData.timeoutId);
      }
      return {
        typingUsers: {
          ...state.typingUsers,
          [conversationId]: {
            ...state.typingUsers[conversationId],
            [userId]: null
          }
        }
      };
    });
  },

  handleMessagesRead: (conversationId, userId) => {
    set((state) => ({
      conversations: state.conversations.map(conv =>
        conv._id === conversationId
          ? { ...conv, unreadCount: 0 }
          : conv
      )
    }));
  },

  handleMessageEdited: (messageId, content, editedAt) => {
    set(state => ({
      messages: state.messages.map(m =>
        m._id === messageId
          ? { ...m, content, editedAt, isEdited: true }
          : m
      )
    }));
  },

  handleReactionUpdated: (messageId, reactions) => {
    set(state => ({
      messages: state.messages.map(m =>
        m._id === messageId ? { ...m, reactions } : m
      )
    }));
  },

  // Handle message deleted via socket
  handleMessageDeleted: (messageId) => {
    set(state => ({
      messages: state.messages.map(m =>
        m._id === messageId ? { ...m, isDeleted: true } : m
      )
    }));
  }
}));

export default useMessageStore;